import * as React from 'react';
import { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Loader2 } from 'lucide-react';
import { API } from 'aws-amplify';
import * as mutations from '../../../graphql/mutations';
import * as utils from '../../../application/common/Utilities';
import getUserAvatar from '../../../application/common/utils/getUserAvatar';
import fetchProfile from '../../../application/routes/profile/fetchProfile';
import { addSeat } from '../../../application/routes/settings/components/PaymentMethods/paymentApi';
import { isStarterAccount } from '../../../application/common/utils/isStarterAccount';
import {
  addUserAction,
  updateUserAction,
  updateProfileAction,
  selectAllUsers,
  selectAllProfiles,
} from '../../../application/store/reducers/users';
import { useToast } from '../../components/ui/toast';
import {
  PersonProfileCard,
  PersonProfileValues,
} from '../../components/person-profile-card';

declare const global: {
  me: {
    id: string;
    orgID: string;
    imageName?: string;
    name: string;
  };
};

const phoneRegEx = /^\+?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/im;

function validate(values: PersonProfileValues): Record<string, string> {
  const errors: Record<string, string> = {};
  if (!values.name || values.name.trim().length < 2) {
    errors.name = 'Name is required (at least 2 characters)';
  }
  if (!values.email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
    errors.email = 'Please enter a valid email address';
  }
  if (!values.phoneNumber || !phoneRegEx.test(values.phoneNumber)) {
    errors.phoneNumber = 'Please enter a valid phone number';
  }
  return errors;
}

export function PersonFormPage() {
  const { userId } = useParams<{ userId?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const toast = useToast();

  const purchaseSeatOnCreate =
    (location.state as any)?.purchaseSeatOnCreate === true;

  const reduxUsers = useSelector(selectAllUsers);
  const reduxProfiles = useSelector(selectAllProfiles);

  const isEditing = !!userId;

  const [values, setValues] = useState<PersonProfileValues>({
    id: '',
    name: '',
    email: '',
    phoneNumber: '',
    title: '',
    location: '',
    imageName: '',
    imageType: '',
    avatarFile: '',
    initials: '',
    functionalRoles: '',
  });
  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSaving, setIsSaving] = useState(false);
  const [teams, setTeams] = useState<any[]>([]);
  const [showTeamsModal, setShowTeamsModal] = useState(false);

  // Load existing user data for edit mode
  useEffect(() => {
    if (!userId) {
      return;
    }

    async function loadUser() {
      setIsLoading(true);
      try {
        const user = await fetchProfile(userId!);
        if (user) {
          setValues({
            id: user.id,
            name: user.name || '',
            email: user.email || '',
            phoneNumber: user.phoneNumber || '',
            title: user.title || '',
            location: user.location || '',
            imageName: user.imageName || '',
            imageType: user.imageType || '',
            avatarFile: user.avatarFile || '',
            initials: user.initials || '',
            functionalRoles: Array.isArray(user.functionalRoles)
              ? user.functionalRoles.join('_')
              : user.functionalRoles || '',
          });
        }
      } catch (err) {
        console.error('Error loading user:', err);
        toast.error('Failed to load user data');
      } finally {
        setIsLoading(false);
      }
    }

    loadUser();
  }, [userId]);

  const handleSave = async () => {
    const validationErrors = validate(values);
    if (Object.keys(validationErrors).length > 0) {
      toast.error('Please fix the errors before saving');
      return;
    }

    setIsSaving(true);
    try {
      if (isEditing) {
        // Strip avatarFile (rendered URL, not a DB field) before sending
        const { avatarFile: _avatarFile, ...personPayload } = values;
        await API.graphql({
          query: mutations.modifyPerson,
          variables: { person: JSON.stringify(personPayload) },
        });

        const originalUser = reduxUsers[values.id];
        if (originalUser) {
          const { functionalRoles: fr, ...rest } = values;
          const storeValues = {
            ...rest,
            functionalRoles: fr ? fr.split('_') : [],
          };
          dispatch(updateUserAction({ ...originalUser, ...storeValues }));
        }
        const originalProfile = reduxProfiles[values.id];
        if (originalProfile) {
          const { functionalRoles: fr2, ...rest2 } = values;
          const storeValues2 = {
            ...rest2,
            functionalRoles: fr2 ? fr2.split('_') : [],
          };
          dispatch(
            updateProfileAction({
              ...originalProfile,
              user: { ...originalProfile.user, ...storeValues2 },
            })
          );
        }

        toast.success(`${values.name} updated successfully`);
      } else {
        // If seat purchase is needed (PLG with no seats), buy the seat first
        if (purchaseSeatOnCreate) {
          try {
            const addSeatRes = await addSeat(global.me.orgID, 1);
            if (!addSeatRes?.success) {
              toast.error(
                addSeatRes?.error ||
                  'Failed to purchase seat. Please try again.'
              );
              setIsSaving(false);
              return;
            }
          } catch (seatErr) {
            console.error('Error purchasing seat:', seatErr);
            toast.error('Failed to purchase seat. Please try again.');
            setIsSaving(false);
            return;
          }
        }

        const names = values.name.split(' ');
        let initials = '';
        if (names.length > 1) {
          initials = names[0][0] + names[1][0];
        } else if (names.length === 1) {
          initials = names[0][0];
        }

        const cleanedPhone = utils.cleanPhoneNumber(values.phoneNumber);

        const newUser = {
          username: values.email,
          email: values.email,
          phoneNumber: cleanedPhone,
          name: values.name,
          birthdate: '',
          imageName: values.imageName,
          imageType: values.imageType,
          location: values.location,
          initials,
          pronoun: '',
          phoneNumberVisibility: 'private',
          emailVisibility: 'private',
          birthdateVisibility: 'circles',
          locationVisibility: 'circles',
          pictureVisibility: 'circles',
          title: values.title,
          avatarID: '',
          orgID: global.me.orgID,
          levelID: '',
          autoEnable: true,
          functionalRoles: '',
        };

        const res = await API.graphql({
          query: mutations.createPerson,
          variables: { person: JSON.stringify(newUser) },
        });
        const result = JSON.parse((res as any).data.createPerson);

        if (result.status === 'success') {
          if (teams.length > 0) {
            const teamRequests = teams.map((team) =>
              API.graphql({
                query: mutations.modifyCircleMember,
                variables: {
                  circleID: team.id,
                  personID: result.user_id,
                  status: 'accepted',
                  role: '',
                },
              })
            );
            await Promise.all(teamRequests).catch((err) =>
              console.error('Error adding user to teams:', err)
            );
          }

          const userForStore: any = {
            ...newUser,
            id: result.user_id,
            status: 'active',
            createdAt: new Date().toISOString(),
            orgs: { items: [{ status: 'accepted', memberType: 'member' }] },
          };
          if (newUser.imageName) {
            userForStore.avatar = await getUserAvatar(
              newUser.imageName,
              newUser.imageType,
              true
            );
          }
          dispatch(addUserAction(userForStore));
          toast.success(
            `${values.name} created successfully. They can now access PAIV.`
          );
        } else {
          toast.error(result.message || 'Failed to create user');
          setIsSaving(false);
          return;
        }
      }

      setTimeout(() => navigate(-1), 500);
    } catch (err) {
      console.error('Error saving user:', err);
      toast.error('There was a problem saving this user');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
        <div className="bg-background rounded-2xl p-12">
          <Loader2 className="size-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-6">
      <PersonProfileCard
        mode="edit"
        values={values}
        onValuesChange={setValues}
        onSave={handleSave}
        onCancel={handleCancel}
        isSaving={isSaving}
        title={isEditing ? 'Edit Profile' : 'New Profile'}
        teams={teams}
        onAddTeams={() => setShowTeamsModal(true)}
        showTeamSelector={!isEditing && !isStarterAccount()}
      />

      {/* Teams Modal */}
      {showTeamsModal && (
        <AddMemberLinkTeamModalWrapper
          teams={teams}
          onSubmit={(newTeams: any[]) => {
            setTeams(newTeams);
            setShowTeamsModal(false);
          }}
          onClose={() => setShowTeamsModal(false)}
        />
      )}
    </div>
  );
}

// ── Teams Modal Wrapper ─────────────────────────────────────

const AddMemberLinkTeamModal = React.lazy(
  () => import('../../../application/routes/teams/AddMemberLinkTeamModal')
);

function AddMemberLinkTeamModalWrapper({
  teams,
  onSubmit,
  onClose,
}: {
  teams: any[];
  onSubmit: (teams: any[]) => void;
  onClose: () => void;
}) {
  return (
    <React.Suspense fallback={null}>
      <AddMemberLinkTeamModal
        title="Add to Teams"
        team={null}
        teamLevelNumIdMap={null}
        teamLevelNumNameMap={null}
        noAutoSelect={true}
        pickCircles={true}
        pickUsers={false}
        update={null}
        filter={(global as any).me.orgID}
        itemValue=""
        onSubmit={onSubmit}
        hideInitialChecked
        initialActiveMembers={teams}
        setShowModal={onClose}
        showModal={true}
      />
    </React.Suspense>
  );
}
